import React, { useEffect, useState } from "react";
import SwipeableViews from "react-swipeable-views";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import {
  APPS,
  APPSRoutes,
  ChangesData,
  FacilityData
} from "components/Conditions/constants";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import ManagementHeader from "components/Conditions/ManagementHeader";
import FacilitySelector from "components/Conditions/FacilitySelector";
import ApplicationSelector from "components/Conditions/ApplicationSelector";
import TrackConditions from "components/Conditions/TrackConditions";
import WorkoutScheduling from "components/Conditions/WorkoutScheduling";
import { isFormChanged } from "components/Conditions/utils";
import { useRacehorse360Api } from "hooks/api";
import useStyles from "./styles";

const ManageTracksPage = () => {
  const classes = useStyles();

  const { currentUser } = useLoggedInUser();
  const facilityIds = currentUser?.facilityIdsListFromRoles;
  const isManagementMode =
    currentUser.isRacingOfficial ||
    currentUser.isVeterinarian ||
    currentUser.isAdmin;

  //appSelector:
  const [currentActiveApp, setCurrentActiveApp] = useState<APPS | null>(null);
  //facilityData from useListFacilities
  const [facilityData, setFacilityData] = useState<FacilityData>({});
  //facilitySelector:
  const [selectedFacility, setSelectedFacility] = useState<string>("");
  const [openState, setOpenState] = useState<string[]>([]);
  const [changesStateCourses, setChangesStateCourses] = useState<ChangesData>(
    {}
  );
  const [isOpenUnsavedModal, setIsOpenUnsavedModal] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const { useListFacilities } = useRacehorse360Api();

  useListFacilities(
    "listFacilitiesOnConditions",
    {
      query: {
        ids: facilityIds,
        isActive: { value: true }
      },
      pagingOptions: {
        maxResults: 10
      },
      getOptions: {
        select: [
          "name",
          "code",
          "backgroundColor",
          "foregroundColor",
          "strokeColor"
        ]
      }
    },
    {
      enabled: facilityIds.length > 0 || currentUser.isAdmin,
      onSuccess: (data: racehorse360.IListFacilitiesResponse) => {
        const facilityData = data.facilities.reduce((reducer, item) => {
          reducer[item.id] = item;
          return reducer;
        }, {});
        setFacilityData(facilityData as FacilityData);
        setCurrentActiveApp(APPS.FacilitySelector);
      },
      onError: error => console.error(error)
    }
  );

  const handleCloseConditions = event => {
    event.preventDefault();
    setFacilityData({});
    setCurrentActiveApp(null);
    setSelectedFacility("");
  };

  const changeSlide = (appName: APPS) => {
    appName === APPS.FacilitySelector && setSelectedFacility("");
    setCurrentActiveApp(appName);
    setActiveTabIndex(getSlideIndex(appName));
  };

  function getSlideIndex(appName: APPS): number {
    if (appName) {
      if (appName === APPS.FacilitySelector) return 0;
      else if (appName === APPS.ApplicationSelector) return 1;
      else if (appName === APPS.TrackConditions) return 2;
      else return 3;
    }

    return 0;
  }

  const getSlideAppName = (index: number): APPS => {
    switch (index) {
      case 1:
        return APPS.ApplicationSelector;
      case 2:
        return APPS.TrackConditions;
      case 3:
        return APPS.WorkoutScheduling;
      default:
        return APPS.FacilitySelector;
    }
  };

  const handleApplicationClick = (appName: APPS, isSaveButtonClicked) => {
    if (currentActiveApp === appName) return;

    if (currentActiveApp === APPS.TrackConditions) {
      const hasChanges = isFormChanged(changesStateCourses);

      if (hasChanges && !isSaveButtonClicked) {
        setIsOpenUnsavedModal(true);
      } else {
        changeSlide(appName);
      }
    } else {
      changeSlide(appName);
    }
  };

  const handleRemoveFromOpenState = (id: string) => {
    setOpenState(openState.filter(it => it !== id));
  };

  const handleAddToOpenState = (id: string) => {
    setOpenState([...openState, id]);
  };

  const handleSlideChange = (appName: APPS) => {
    setActiveTabIndex(getSlideIndex(appName));
  };

  const handleSwipeSlideChange = (index: number) => {
    !index && setSelectedFacility("");

    setCurrentActiveApp(getSlideAppName(index));
    setActiveTabIndex(index);
  };

  useEffect(() => {
    //option with history.replace() re-render the page
    currentActiveApp &&
      window.history.replaceState(
        null,
        APPSRoutes.PageTitle,
        APPSRoutes[currentActiveApp]
      );
  }, [currentActiveApp]);

  return (
    <AppPage>
      <AppPageContent>
        <div className={classes.managementTracksContent}>
          <ManagementHeader
            selectedFacility={selectedFacility}
            facilityData={facilityData}
            currentActiveApp={currentActiveApp}
            onCloseClick={handleCloseConditions}
            onApplicationClick={handleApplicationClick}
            isManagementMode={isManagementMode}
            disabled={openState.length > 0}
          />

          <SwipeableViews
            slideClassName={classes.swipeableSlide}
            index={activeTabIndex}
            onChangeIndex={handleSwipeSlideChange}
            disabled={true}
          >
            <FacilitySelector
              facilityData={facilityData}
              setSelectedFacility={setSelectedFacility}
              setCurrentActiveApp={setCurrentActiveApp}
              onSlideChange={handleSlideChange}
            />
            <ApplicationSelector
              setCurrentActiveApp={setCurrentActiveApp}
              onSlideChange={handleSlideChange}
            />
            <TrackConditions
              facilityData={facilityData}
              selectedFacility={selectedFacility}
              facilityIds={facilityIds}
              onRemoveFromOpenState={handleRemoveFromOpenState}
              onAddToOpenState={handleAddToOpenState}
              setCurrentActiveApp={setCurrentActiveApp}
              onChangesState={setChangesStateCourses}
              changesState={changesStateCourses}
              onOpenUnsavedModal={setIsOpenUnsavedModal}
              isOpenUnsavedModal={isOpenUnsavedModal}
              onApplicationClick={handleApplicationClick}
              onChangeSlide={changeSlide}
              openState={openState}
              currentActiveApp={currentActiveApp}
            />
            <WorkoutScheduling
              facilityData={facilityData}
              selectedFacility={selectedFacility}
            />
          </SwipeableViews>
        </div>
      </AppPageContent>
    </AppPage>
  );
};

export default ManageTracksPage;
